import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import PanelSidebar from '../components/PanelSidebar';
import { Eye, Package, Calendar, User, CheckCircle, Clock, XCircle, Link as LinkIcon, Search, Filter, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ah = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const kullaniciAd = (u) => !u ? '-' : (typeof u === 'string' ? u : (u.user_name || u.name || u.ad || u.kullanici_adi || u.email || '-'));
// Logo/ERP'den gelen alanlar bazen OBJE olabiliyor → doğrudan render "Objects are not
// valid as a React child (#31)" ile beyaz ekran veriyordu. Her zaman güvenli string döndür.
const guvenliMetin = (v) => {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'object') return v.unvan || v.ad || v.name || v.user_name || v.title || v.definition_ || '';
  return String(v);
};

const Siparislerim = () => {
  // 1. Hooks
  const navigate = useNavigate();
  const { tip, durum } = useParams();
  const { isFirmaUser, getUserFirma, isMusteriUser, user } = useAuth();

  // 2. State
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [siparisler, setSiparisler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seciliSiparis, setSeciliSiparis] = useState(null);
  const [aktifSekme, setAktifSekme] = useState('bekleyen');
  const [tarihFiltre, setTarihFiltre] = useState('tumu'); // gun, hafta, ay, yil, tumu
  const [aramaMetni, setAramaMetni] = useState('');

// 3. Fonksiyonlar
  const fetchSiparisler = useCallback(async () => {
    setLoading(true);
    try {
      // Müşteri kendi siparişlerini görür
      const cariKodu = user?.cari_kodu;
      if (!cariKodu) {
        console.error('Cari kodu bulunamadı');
        toast.error('Cari kod bilgisi eksik');
        return;
      }
      
      const url = `${API}/panel/musteri-siparislerim/${cariKodu}`;
      console.log('Müşteri siparişleri çekiliyor:', url);
      const response = await axios.get(url);
      if (response.data.success) {
        setSiparisler(response.data.data);
        console.log('Siparişler yüklendi:', response.data.data.length);
      }
    } catch (error) {
      console.error('Siparişlerim yüklenemedi:', error);
      toast.error('Siparişleriniz yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [user]);


  const getDurumBilgi = (durum) => {
    switch (durum) {
      case 'beklemede': 
        return { renk: 'bg-yellow-100 border-yellow-300 text-yellow-800', icon: Clock, label: 'Bekleyen' };
      case 'transfer_beklemede': 
        return { renk: 'bg-amber-100 border-amber-300 text-amber-800', icon: Clock, label: 'Transfer Bekliyor' };
      case 'transfer_bekliyor':
        return { renk: 'bg-orange-100 border-orange-300 text-orange-800', icon: Clock, label: 'Transfer Bekliyor' };
      case 'kismen_hazir': 
      case 'hazirlaniyor': 
        return { renk: 'bg-blue-100 border-blue-300 text-blue-800', icon: Package, label: 'Hazırlanıyor' };
      case 'tamamlandi': 
      case 'sevk_edildi':
        return { renk: 'bg-green-100 border-green-300 text-green-800', icon: CheckCircle, label: 'Tamamlandı' };
      case 'iptal': 
        return { renk: 'bg-red-100 border-red-300 text-red-800', icon: XCircle, label: 'İptal' };
      default: 
        return { renk: 'bg-gray-100 border-gray-300 text-gray-800', icon: Package, label: durum };
    }
  };
    
  useEffect(() => {
    if (durum) setAktifSekme(durum);
    fetchSiparisler();
  }, [tip, durum, fetchSiparisler]);



  const filtreliSiparisler = useMemo(() => {
    if (!siparisler) return [];

    // Tarih filtreleme fonksiyonu
    const tarihFiltrele = (siparis) => {
      if (tarihFiltre === 'tumu') return true;
      
      const siparisTarih = new Date(siparis.tarih);
      const bugun = new Date();
      const fark = bugun - siparisTarih;
      const gunFark = Math.floor(fark / (1000 * 60 * 60 * 24));
      
      switch (tarihFiltre) {
        case 'gun': return gunFark <= 1;
        case 'hafta': return gunFark <= 7;
        case 'ay': return gunFark <= 30;
        case 'yil': return gunFark <= 365;
        default: return true;
      }
    };

    let filtered = siparisler;

    // Durum filtreleme
    switch (aktifSekme) {
      case 'bekleyen':
        filtered = filtered.filter(s => ['beklemede', 'transfer_beklemede', 'transfer_bekliyor'].includes(s.durum));
        break;
      case 'hazirlanan':
        filtered = filtered.filter(s => ['kismen_hazir', 'hazirlaniyor', 'transfer_tamamlandi'].includes(s.durum));
        break;
      case 'tamamlanan':
        filtered = filtered.filter(s => ['tamamlandi', 'sevk_edildi'].includes(s.durum));
        break;
      case 'iptal':
        filtered = filtered.filter(s => s.durum === 'iptal');
        break;
      default:
        break;
    }

    // Tarih filtreleme (özellikle tamamlanan için)
    if (aktifSekme === 'tamamlanan' && tarihFiltre !== 'tumu') {
      filtered = filtered.filter(tarihFiltrele);
    }

    // Arama filtreleme
    if (aramaMetni.trim()) {
      const arama = aramaMetni.toLowerCase();
      filtered = filtered.filter(s => 
        s.siparis_no?.toLowerCase().includes(arama) ||
        s.musteri_adi?.toLowerCase().includes(arama) ||
        s.cari_kodu?.toLowerCase().includes(arama)
      );
    }

    return filtered;
  }, [siparisler, aktifSekme, tarihFiltre, aramaMetni]);

  return (
    <div className="flex h-screen bg-gray-100">
      <PanelSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <main className="flex-1 overflow-y-auto">
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-5 flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Sipariş Yönetimi</h1>
              <p className="text-sm text-gray-500 mt-1">{siparisler.length} sipariş</p>
            </div>
            {/* Müşteri panelinde Yeni Sipariş butonu gizli - Siparişler Speetim üzerinden gelir */}
            {!isMusteriUser?.() && (
              <button onClick={() => navigate('/panel/yeni-siparis')} className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium">
                + Yeni Sipariş
              </button>
            )}
          </div>
        </div>

        <div className="max-w-7xl mx-auto p-6 space-y-6">
          <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
            <div className="flex gap-3 mb-4">
              <button onClick={() => setAktifSekme('bekleyen')} className={`px-5 py-2.5 rounded-xl text-sm font-medium ${aktifSekme === 'bekleyen' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg' : 'bg-white text-gray-700 border'}`}>
                📋 Bekleyen ({siparisler.filter(s => ['beklemede', 'transfer_beklemede', 'transfer_bekliyor'].includes(s.durum)).length})
              </button>
              <button onClick={() => setAktifSekme('hazirlanan')} className={`px-5 py-2.5 rounded-xl text-sm font-medium ${aktifSekme === 'hazirlanan' ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg' : 'bg-white text-gray-700 border'}`}>
                🔄 Hazırlanan ({siparisler.filter(s => ['kismen_hazir', 'hazirlaniyor'].includes(s.durum)).length})
              </button>
              <button onClick={() => setAktifSekme('tamamlanan')} className={`px-5 py-2.5 rounded-xl text-sm font-medium ${aktifSekme === 'tamamlanan' ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg' : 'bg-white text-gray-700 border'}`}>
                ✅ Tamamlanan ({siparisler.filter(s => ['tamamlandi', 'sevk_edildi'].includes(s.durum)).length})
              </button>
              <button onClick={() => setAktifSekme('iptal')} className={`px-5 py-2.5 rounded-xl text-sm font-medium ${aktifSekme === 'iptal' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg' : 'bg-white text-gray-700 border'}`}>
                ❌ İptal ({siparisler.filter(s => s.durum === 'iptal').length})
              </button>
            </div>
            
            {/* Filtre ve Arama Kontrolleri */}
            <div className="flex gap-3 items-center">
              {/* Arama */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Sipariş no, müşteri adı veya cari kodu ara..."
                  value={aramaMetni}
                  onChange={(e) => setAramaMetni(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              {/* Tarih Filtresi - Sadece tamamlanan sekmesinde göster */}
              {aktifSekme === 'tamamlanan' && (
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-gray-500" />
                  <select
                    value={tarihFiltre}
                    onChange={(e) => setTarihFiltre(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="tumu">Tüm Zamanlar</option>
                    <option value="gun">Son 24 Saat</option>
                    <option value="hafta">Son 7 Gün</option>
                    <option value="ay">Son 30 Gün</option>
                    <option value="yil">Son 1 Yıl</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Modern Sipariş Listesi */}
          <div className="space-y-3">
            {loading ? (
              <div className="bg-white rounded-xl p-12 text-center border-2 border-gray-200">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Siparişler yükleniyor...</p>
              </div>
            ) : filtreliSiparisler.length > 0 ? (
              filtreliSiparisler.map((s) => {
                const durumBilgi = getDurumBilgi(s.durum);
                const DurumIcon = durumBilgi.icon;
                
                return (
                  <div 
                    key={s._id} 
                    className="bg-white rounded-xl border-2 border-gray-200 hover:border-indigo-400 hover:shadow-lg transition-all p-4 cursor-pointer"
                    onClick={() => setSeciliSiparis(s)}
                  >
                    <div className="flex items-center justify-between">
                      {/* Sol: Sipariş Bilgileri */}
                      <div className="flex items-center gap-4 flex-1">
                        {/* Sipariş No */}
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500 mb-1">Sipariş No</span>
                          <span className="font-mono text-sm font-bold text-indigo-600">{s.siparis_no}</span>
                        </div>

                        {/* Tarih */}
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500 mb-1">Tarih</span>
                          <div className="flex items-center gap-1 text-sm text-gray-700">
                            <Calendar size={14} />
                            <span>
                              {(() => {
                                const tarih = new Date(s.tarih || s.created_at);
                                if (isNaN(tarih.getTime())) return 'Tarih yok';
                                return `${tarih.toLocaleDateString('tr-TR', {
                                  day: '2-digit',
                                  month: '2-digit', 
                                  year: 'numeric'
                                })} ${tarih.toLocaleTimeString('tr-TR', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}`;
                              })()}
                            </span>
                          </div>
                        </div>

                        {/* Müşteri/Kaynak-Hedef */}
                        <div className="flex flex-col flex-1 max-w-xs">
                          <span className="text-xs text-gray-500 mb-1">
                            {s.tip === 'satis' ? 'Müşteri' : 'Kaynak → Hedef'}
                          </span>
                          <div className="flex items-center gap-1 text-sm text-gray-800 font-medium truncate">
                            <User size={14} />
                            {s.tip === 'satis' ? (
                              <span className="truncate">{guvenliMetin(s.musteri_adi) || guvenliMetin(s.hedef) || '-'}</span>
                            ) : (
                              <span className="truncate">{s.kaynak} → {s.hedef}</span>
                            )}
                          </div>
                        </div>

                        {/* İşlem Yapan */}
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500 mb-1">İşlem Yapan</span>
                          <div className="flex items-center gap-1 text-sm text-gray-700">
                            <User size={14} className="text-indigo-600" />
                            <span className="font-medium">{kullaniciAd(s.created_by || s.kullanici_adi)}</span>
                          </div>
                        </div>

                        {/* Ürün Sayısı */}
                        <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                          <Package size={16} className="text-gray-600" />
                          <span className="text-sm font-bold text-gray-800">{s.urunler?.length || 0}</span>
                          <span className="text-xs text-gray-500">ürün</span>
                        </div>

                        {/* Tutar */}
                        <div className="flex flex-col items-end">
                          <span className="text-xs text-gray-500 mb-1">Toplam</span>
                          <span className="text-base font-bold text-green-600">
                            {(s.toplam_tutar || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} TRY
                          </span>
                        </div>
                      </div>

                      {/* Sağ: Durum ve İşlemler */}
                      <div className="flex items-center gap-3 ml-4">
                        {/* Durum Badge */}
                        <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border-2 ${durumBilgi.renk}`}>
                          <DurumIcon size={16} />
                          {durumBilgi.label}
                        </span>

                        {/* Detay Butonu */}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSeciliSiparis(s);
                          }}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white p-3 rounded-lg shadow-md hover:shadow-lg transition-all"
                          data-testid={`siparis-detay-btn-${s.siparis_no}`}
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </div>
                    {/* İptal Gerekçesi - Kart altında */}
                    {s.durum === 'iptal' && s.iptal_nedeni && (
                      <div className="mt-3 pt-3 border-t border-red-200 flex items-start gap-2" data-testid={`iptal-neden-kart-${s.siparis_no}`}>
                        <XCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-xs font-semibold text-red-600">İptal Gerekçesi: </span>
                          <span className="text-xs text-red-700">{s.iptal_nedeni}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="bg-white rounded-xl p-12 text-center border-2 border-gray-200">
                <Package size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Sipariş bulunamadı</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {seciliSiparis && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSeciliSiparis(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            
            {/* Header - Modern gradient */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Package size={32} />
                <div>
                  <h2 className="text-2xl font-bold">Sipariş Detayı</h2>
                  <p className="text-sm text-blue-100 font-mono mt-1">{seciliSiparis.siparis_no}</p>
                </div>
                {/* Durum Badge */}
                {(() => {
                  const durumBilgi = getDurumBilgi(seciliSiparis.durum);
                  const DurumIcon = durumBilgi.icon;
                  return (
                    <div className="ml-auto mr-4 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg flex items-center gap-2">
                      <DurumIcon size={20} />
                      <span className="font-semibold text-lg">{durumBilgi.label}</span>
                    </div>
                  );
                })()}
              </div>
              <button 
                onClick={() => setSeciliSiparis(null)} 
                className="p-2 hover:bg-white/20 rounded-full transition"
              >
                <X size={24} />
              </button>
            </div>

            {/* İptal Gerekçesi Banner */}
            {seciliSiparis.durum === 'iptal' && (
              <div className="mx-6 mt-4 bg-red-50 border-2 border-red-300 rounded-xl p-4" data-testid="iptal-gerekce-banner">
                <div className="flex items-start gap-3">
                  <XCircle size={24} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-red-700 mb-1">Bu sipariş iptal edilmiştir</h4>
                    {seciliSiparis.iptal_nedeni && (
                      <p className="text-sm text-red-600" data-testid="iptal-gerekce-metin">
                        <span className="font-semibold">Gerekçe: </span>{seciliSiparis.iptal_nedeni}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-red-500">
                      {seciliSiparis.iptal_tarihi && (
                        <span>
                          {(() => {
                            const t = new Date(seciliSiparis.iptal_tarihi);
                            return isNaN(t.getTime()) ? '' : t.toLocaleString('tr-TR');
                          })()}
                        </span>
                      )}
                      {seciliSiparis.iptal_eden && (
                        <span>İptal eden: {seciliSiparis.iptal_eden}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Özet Bilgiler - 4 Kart */}
            <div className="grid grid-cols-4 gap-3 p-6 bg-gray-50 border-b-2 border-gray-200">
              <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                <p className="text-xs text-gray-500 mb-1">Müşteri</p>
                <p className="text-sm font-bold text-gray-900 truncate" title={guvenliMetin(seciliSiparis.musteri_adi)}>
                  {guvenliMetin(seciliSiparis.musteri_adi) || guvenliMetin(seciliSiparis.hedef) || 'N/A'}
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                <p className="text-xs text-gray-500 mb-1">Tarih & Saat</p>
                {(() => {
                  const tarih = new Date(seciliSiparis.tarih || seciliSiparis.created_at);
                  if (isNaN(tarih.getTime())) {
                    return <p className="text-sm text-red-500">Tarih bilgisi yok</p>;
                  }
                  return (
                    <>
                      <p className="text-sm font-bold text-gray-900">
                        {tarih.toLocaleDateString('tr-TR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {tarih.toLocaleTimeString('tr-TR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </>
                  );
                })()}
              </div>
              
              <div className="bg-white rounded-lg p-4 border-2 border-purple-200">
                <p className="text-xs text-gray-500 mb-1">Ürün Sayısı</p>
                <p className="text-2xl font-bold text-purple-600">{seciliSiparis.urunler?.length || 0}</p>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-2 border-emerald-200">
                <p className="text-xs text-gray-500 mb-1">Toplam Tutar</p>
                <p className="text-lg font-bold text-green-600">{formatMoney(seciliSiparis.toplam_tutar)}</p>
              </div>
            </div>
            {/* Body - Ürün Listesi */}
            <div className="flex-1 overflow-y-auto p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Package size={20} className="text-indigo-600" />
                Sipariş İçeriği
              </h3>

              {/* Ürünler - Kart Görünümü */}
              <div className="space-y-3">
                {(Array.isArray(seciliSiparis.urunler) ? seciliSiparis.urunler : []).filter(Boolean).map((u, i) => {
                  // Ürünün hangi depodan geldiğini bul
                  const dagitim = Array.isArray(seciliSiparis.dagitim_plani)
                    ? seciliSiparis.dagitim_plani.find(p => p && p.sku === u.sku) : null;
                  const kaynakBilgi = Array.isArray(dagitim?.dagitim) ? dagitim.dagitim[0] : null;
                  
                  return (
                    <div key={i} className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-indigo-300 transition-all">
                      <div className="flex items-center justify-between">
                        {/* Sol: Ürün Bilgileri */}
                        <div className="flex-1">
                          <h4 className="text-sm font-bold text-gray-900 mb-1">{u.urun_adi}</h4>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-gray-500 font-mono">SKU: {u.sku}</p>
                            {/* YETSAN / ITHAL Badge */}
                            {u.groupCode && (
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                u.groupCode.toUpperCase().includes('YETSAN') 
                                  ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                                  : u.groupCode.toUpperCase().includes('ITHAL') 
                                    ? 'bg-orange-100 text-orange-700 border border-orange-300'
                                    : 'bg-gray-100 text-gray-600'
                              }`}>
                                {u.groupCode.toUpperCase().includes('YETSAN') ? 'YETSAN' : 
                                 u.groupCode.toUpperCase().includes('ITHAL') ? 'ITHAL' : u.groupCode}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Orta: Adet Badge */}
                        <div className="flex items-center gap-2 mx-4">
                          <div className="bg-blue-100 border-2 border-blue-300 rounded-lg px-4 py-2">
                            <p className="text-xs text-blue-600 mb-1">Adet</p>
                            <p className="text-lg font-bold text-blue-700">{u.miktar}</p>
                          </div>
                        </div>

                        {/* Kaynak Bilgisi (Sadece firma için) */}
                        {!isMusteriUser() && kaynakBilgi && (
                          <div className="mx-4">
                            <p className="text-xs text-gray-500 mb-1">Kaynak</p>
                            <span className={`inline-block px-3 py-1 rounded-lg text-xs font-semibold ${
                              kaynakBilgi.tip === 'kendi_stok' 
                                ? 'bg-green-100 text-green-700 border border-green-300' 
                                : 'bg-amber-100 text-amber-700 border border-amber-300'
                            }`}>
                              {kaynakBilgi.depo}
                              {kaynakBilgi.tip === 'transfer' && ' 🔄'}
                            </span>
                          </div>
                        )}

                        {/* Sağ: Fiyat */}
                        <div className="text-right">
                          <p className="text-xs text-gray-500 mb-1">Toplam</p>
                          <p className="text-lg font-bold text-green-600">
                            {formatMoney((u.miktar || 0) * (u.birim_fiyat || 0))}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* İşlem Yapan Bilgisi */}
              {seciliSiparis.created_by && (
                <div className="mt-4 bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm">
                    <User size={16} className="text-blue-600" />
                    <span className="text-gray-600">Siparişi Oluşturan:</span>
                    <span className="font-semibold text-gray-900">{kullaniciAd(seciliSiparis.created_by)}</span>
                    <span className="text-gray-500 ml-auto">
                      {new Date(seciliSiparis.created_at || seciliSiparis.tarih).toLocaleString('tr-TR')}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer - Onay Butonları */}
            <div className="bg-gray-50 px-6 py-4 border-t-2 border-gray-200 rounded-b-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* İşlem Geçmişi Info */}
                {seciliSiparis.updated_by && (
                  <div className="text-sm text-gray-600">
                    Son güncelleme: <span className="font-semibold">{kullaniciAd(seciliSiparis.updated_by)}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                {/* Kapat Butonu */}
                <button 
                  onClick={() => setSeciliSiparis(null)}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition"
                >
                  Kapat
                </button>

                {/* Onay Butonları - Sadece firma kullanıcıları */}
                {!isMusteriUser() && (() => {
                  // Transfer ise sadece kaynak firma onaylar
                  if (seciliSiparis.tip === 'transfer') {
                    if (getUserFirma() === seciliSiparis.kaynak && seciliSiparis.durum === 'transfer_beklemede') {
                      return (
                        <button 
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/panel/siparis-onayla/${seciliSiparis._id}`, {}, ah());
                              toast.success('✅ Transfer onaylandı!');
                              setSeciliSiparis(null);
                              fetchSiparisler();
                            } catch (err) {
                              toast.error('Onaylama başarısız');
                            }
                          }} 
                          className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                        >
                          <CheckCircle size={20} />
                          Transferi Onayla
                        </button>
                      );
                    }
                    return null;
                  }
                  
                  // Ana sipariş: Bekleyen durumda onaylanabilir
                  if (seciliSiparis.durum === 'beklemede') {
                    return (
                      <>
                        {/* Red Butonu */}
                        <button 
                          onClick={async () => {
                            const neden = window.prompt('İptal gerekçesini yazınız:');
                            if (neden === null) return; // Kullanıcı Cancel'a bastı
                            try {
                              await axios.put(`${API}/panel/siparis-iptal/${seciliSiparis._id}`, {
                                iptal_nedeni: neden || 'Belirtilmedi'
                              });
                              toast.success('Sipariş iptal edildi');
                              setSeciliSiparis(null);
                              fetchSiparisler();
                            } catch (err) {
                              toast.error('İptal işlemi başarısız');
                            }
                          }} 
                          className="px-6 py-3 bg-white border-2 border-red-300 text-red-600 hover:bg-red-50 rounded-lg font-semibold transition flex items-center gap-2"
                          data-testid="siparis-iptal-btn"
                        >
                          <XCircle size={20} />
                          İptal Et
                        </button>

                        {/* Onay Butonu */}
                        <button 
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/panel/siparis-onayla/${seciliSiparis._id}`, {}, ah());
                              toast.success('✅ Sipariş onaylandı ve hazırlanıyor durumuna geçti!');
                              setSeciliSiparis(null);
                              fetchSiparisler();
                            } catch (err) {
                              toast.error('Onaylama başarısız');
                            }
                          }} 
                          className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                        >
                          <CheckCircle size={20} />
                          Siparişi Onayla
                        </button>
                      </>
                    );
                  }
                  
                  return null;
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Siparislerim;
